import type { operations } from "@airange/generated-contracts/edu";
import { eduAdminOpenApiClient } from "../generated/client";
import { omitEmptyQuery } from "./openapi-query";
import { unwrapOpenApiResult } from "./openapi-result";

type OperationResponse<
  TOperation extends { responses: Record<number, unknown> },
  TStatus extends keyof TOperation["responses"],
> = TOperation["responses"][TStatus] extends {
  content: { "application/json": infer TResponse };
}
  ? TResponse
  : never;

type OperationRequestBody<TOperation extends { requestBody?: unknown }> = NonNullable<
  TOperation["requestBody"]
> extends {
  content: { "application/json": infer TBody };
}
  ? TBody
  : never;

type OperationQuery<TOperation extends { parameters: { query?: unknown } }> = NonNullable<
  TOperation["parameters"]["query"]
>;

type OperationPath<TOperation extends { parameters: { path?: unknown } }> = NonNullable<
  TOperation["parameters"]["path"]
>;

type UiNonNullQuery<TQuery extends object, TReplaced extends keyof TQuery = never> = {
  [TKey in Exclude<keyof TQuery, TReplaced>]?: NonNullable<TQuery[TKey]>;
};

type GetTeachingPlansOverviewOperation = operations["get_teaching_plans_overview_api_teaching_plans_overview_get"];
type GetTeachingPlansOperation = operations["get_teaching_plans_api_teaching_plans_get"];
type CreateTeachingPlanOperation = operations["post_teaching_plan_api_teaching_plans_post"];
type GetTeachingPlanOperation = operations["get_teaching_plan_api_teaching_plans__plan_id__get"];
type GetTeachingPlanStatisticsOperation =
  operations["get_teaching_plan_statistics_endpoint_api_teaching_plans__plan_id__statistics_get"];
type BindTeachingPlanMembersOperation =
  operations["put_teaching_plan_members_api_teaching_plans__plan_id__members_put"];
type GetTeachingPlanBoardThreadsOperation =
  operations["get_teaching_plan_board_threads_api_teaching_plans__plan_id__board_threads_get"];
type CreateTeachingPlanBoardThreadOperation =
  operations["post_teaching_plan_board_thread_api_teaching_plans__plan_id__board_threads_post"];
type GetTeachingPlanNoticesOperation =
  operations["get_teaching_plan_notices_api_teaching_plans__plan_id__notices_get"];
type CreateTeachingPlanNoticeOperation =
  operations["post_teaching_plan_notice_api_teaching_plans__plan_id__notices_post"];
type UpdateTeachingPlanSnapshotCatalogOperation =
  operations["patch_teaching_plan_snapshot_catalog_api_teaching_plans__plan_id__snapshot_catalog_patch"];
type GetTeachingPlanTaskSubmissionsOperation =
  operations["get_teaching_plan_task_submissions_api_teaching_plans__plan_id__task_submissions_get"];
type ReviewTeachingPlanTaskSubmissionOperation =
  operations["patch_teaching_plan_task_submission_review_api_teaching_plans__plan_id__task_submissions__submission_id__review_patch"];
type PublishTeachingPlanOperation =
  operations["post_teaching_plan_publish_api_teaching_plans__plan_id__publish_post"];
type EndTeachingPlanOperation = operations["post_teaching_plan_end_api_teaching_plans__plan_id__end_post"];
type ResetCourseLearningOperation =
  operations["post_course_learning_reset_api_learning_records_courses__course_id__members__member_id__reset_post"];
type BulkResetCourseLearningOperation =
  operations["post_course_learning_bulk_reset_api_learning_records_courses__course_id__members_reset_post"];

type TeachingPlansGeneratedQuery = OperationQuery<GetTeachingPlansOperation>;
type TeachingPlanStatisticsGeneratedQuery = OperationQuery<GetTeachingPlanStatisticsOperation>;
type TeachingPlanTaskSubmissionsGeneratedQuery = OperationQuery<GetTeachingPlanTaskSubmissionsOperation>;
type TeachingPlanId = OperationPath<GetTeachingPlanOperation>["plan_id"];
type TeachingPlanSubmissionId = OperationPath<ReviewTeachingPlanTaskSubmissionOperation>["submission_id"];
type CourseId = OperationPath<BulkResetCourseLearningOperation>["course_id"];
type MemberId = OperationPath<ResetCourseLearningOperation>["member_id"];
type CreateTeachingPlanResponse = OperationResponse<CreateTeachingPlanOperation, 201>;
type UpdateTeachingPlanSnapshotCatalogResponse = OperationResponse<UpdateTeachingPlanSnapshotCatalogOperation, 200>;
type BindTeachingPlanMembersResponse = OperationResponse<BindTeachingPlanMembersOperation, 200>;
type CreateTeachingPlanBoardThreadResponse = OperationResponse<CreateTeachingPlanBoardThreadOperation, 201>;
type CreateTeachingPlanNoticeResponse = OperationResponse<CreateTeachingPlanNoticeOperation, 201>;
type PublishTeachingPlanResponse = OperationResponse<PublishTeachingPlanOperation, 200>;
type EndTeachingPlanResponse = OperationResponse<EndTeachingPlanOperation, 200>;

export type EduTeachingPlansListQuery = UiNonNullQuery<TeachingPlansGeneratedQuery, "page_size"> & {
  pageSize?: NonNullable<TeachingPlansGeneratedQuery["page_size"]>;
};
type EduTeachingPlanTaskSubmissionsQuery = UiNonNullQuery<
  TeachingPlanTaskSubmissionsGeneratedQuery,
  "page_size"
> & {
  pageSize?: NonNullable<TeachingPlanTaskSubmissionsGeneratedQuery["page_size"]>;
};
export type EduTeachingPlanStatisticsQuery = UiNonNullQuery<
  TeachingPlanStatisticsGeneratedQuery,
  "learner_page" | "learner_page_size"
> & {
  learnerPage?: NonNullable<TeachingPlanStatisticsGeneratedQuery["learner_page"]>;
  learnerPageSize?: NonNullable<TeachingPlanStatisticsGeneratedQuery["learner_page_size"]>;
};

export type EduTeachingPlansOverview = OperationResponse<GetTeachingPlansOverviewOperation, 200>;
export type EduTeachingPlanOverviewBucket = EduTeachingPlansOverview["buckets"][number];
export type EduTeachingPlansListResponse = OperationResponse<GetTeachingPlansOperation, 200>;
export type EduTeachingPlanListItem = EduTeachingPlansListResponse["items"][number];
export type EduTeachingPlanLifecycleStatus = EduTeachingPlanListItem["lifecycle_status"];
export type EduTeachingPlanProgressionMode = EduTeachingPlanListItem["progression_mode"];
export type EduTeachingPlanCreatePayload = OperationRequestBody<CreateTeachingPlanOperation>;
export type EduTeachingPlanDetail = OperationResponse<GetTeachingPlanOperation, 200>;
export type EduTeachingPlanMemberGroup = EduTeachingPlanDetail["member_groups"][number];
export type EduTeachingPlanMemberCard = EduTeachingPlanMemberGroup["members"][number];
export type EduTeachingPlanMemberStatus = EduTeachingPlanMemberCard["status"];
export type EduTeachingPlanSnapshotCatalogSummary = EduTeachingPlanDetail["snapshot_catalog_summary"];
export type EduTeachingPlanSnapshotCatalog = EduTeachingPlanDetail["snapshot_catalog"];
export type EduTeachingPlanSnapshotCatalogChapter = EduTeachingPlanSnapshotCatalog["chapters"][number];
export type EduTeachingPlanSnapshotCatalogLesson = EduTeachingPlanSnapshotCatalogChapter["lessons"][number];
export type EduTeachingPlanSnapshotCatalogTask = EduTeachingPlanSnapshotCatalogLesson["tasks"][number];
export type EduTeachingPlanRequiredKind = EduTeachingPlanSnapshotCatalogLesson["required_kind"];
export type EduTeachingPlanLessonType = EduTeachingPlanSnapshotCatalogLesson["lesson_type"];
export type EduTeachingPlanTaskType = EduTeachingPlanSnapshotCatalogTask["task_type"];
export type EduTeachingPlanTaskStageKey = EduTeachingPlanSnapshotCatalogTask["stage_key"];
export type EduTeachingPlanAdjustmentTraceItem = EduTeachingPlanDetail["adjustment_trace_items"][number];
export type EduTeachingPlanSnapshotCatalogUpdatePayload =
  OperationRequestBody<UpdateTeachingPlanSnapshotCatalogOperation>;
export type EduTeachingPlanSnapshotCatalogUpdateChapter =
  EduTeachingPlanSnapshotCatalogUpdatePayload["chapters"][number];
export type EduTeachingPlanSnapshotCatalogUpdateLesson =
  EduTeachingPlanSnapshotCatalogUpdateChapter["lessons"][number];
export type EduTeachingPlanSnapshotCatalogUpdateTask =
  EduTeachingPlanSnapshotCatalogUpdateLesson["tasks"][number];
export type EduTeachingPlanMemberBindingPayload = OperationRequestBody<BindTeachingPlanMembersOperation>;
export type EduTeachingPlanBoardThreadsResponse = OperationResponse<GetTeachingPlanBoardThreadsOperation, 200>;
export type EduTeachingPlanBoardThreadItem = EduTeachingPlanBoardThreadsResponse["items"][number];
export type EduTeachingPlanBoardThreadCreatePayload =
  OperationRequestBody<CreateTeachingPlanBoardThreadOperation>;
export type EduTeachingPlanNoticesResponse = OperationResponse<GetTeachingPlanNoticesOperation, 200>;
export type EduTeachingPlanNoticeItem = EduTeachingPlanNoticesResponse["items"][number];
export type EduTeachingPlanNoticeSourceKind = EduTeachingPlanNoticeItem["source_kind"];
export type EduTeachingPlanNoticeCreatePayload = OperationRequestBody<CreateTeachingPlanNoticeOperation>;
export type EduTeachingPlanTaskSubmissionsResponse = OperationResponse<GetTeachingPlanTaskSubmissionsOperation, 200>;
export type EduTeachingPlanTaskSubmissionItem = EduTeachingPlanTaskSubmissionsResponse["items"][number];
export type EduTeachingPlanTaskSubmissionStatus = EduTeachingPlanTaskSubmissionItem["status"];
export type EduTeachingPlanTaskSubmissionReviewPayload =
  OperationRequestBody<ReviewTeachingPlanTaskSubmissionOperation>;
export type EduTeachingPlanTaskSubmissionReviewResponse =
  OperationResponse<ReviewTeachingPlanTaskSubmissionOperation, 200>;
export type EduCourseLearningResetResponse = OperationResponse<ResetCourseLearningOperation, 200>;
export type EduLearningRecordSourceKind = EduCourseLearningResetResponse["source_kind"];
export type EduLearningRecordStatus = EduCourseLearningResetResponse["status"];
export type EduCourseLearningBulkResetResponse = OperationResponse<BulkResetCourseLearningOperation, 200>;
export type EduCourseLearningBulkResetItem = EduCourseLearningBulkResetResponse["items"][number];
export type EduTeachingPlanStatisticsResponse = OperationResponse<GetTeachingPlanStatisticsOperation, 200>;
export type EduTeachingPlanStatisticsMetric = EduTeachingPlanStatisticsResponse["metrics"][number];
export type EduTeachingPlanStatisticsStatusBucket = EduTeachingPlanStatisticsResponse["status_buckets"][number];
export type EduTeachingPlanStatisticsLearnerProgressItem =
  EduTeachingPlanStatisticsResponse["learner_progress_items"][number];

function toTeachingPlansListQueryParams(
  query?: EduTeachingPlansListQuery,
): TeachingPlansGeneratedQuery | undefined {
  if (!query) return undefined;
  const { pageSize, ...generatedQuery } = query;
  return omitEmptyQuery<TeachingPlansGeneratedQuery>({
    ...generatedQuery,
    ...(pageSize === undefined ? {} : { page_size: pageSize }),
  });
}

function toTeachingPlanTaskSubmissionsQueryParams(
  query?: EduTeachingPlanTaskSubmissionsQuery,
): TeachingPlanTaskSubmissionsGeneratedQuery | undefined {
  if (!query) return undefined;
  const { pageSize, ...generatedQuery } = query;
  return omitEmptyQuery<TeachingPlanTaskSubmissionsGeneratedQuery>({
    ...generatedQuery,
    ...(pageSize === undefined ? {} : { page_size: pageSize }),
  });
}

function toTeachingPlanStatisticsQueryParams(
  query?: EduTeachingPlanStatisticsQuery,
): TeachingPlanStatisticsGeneratedQuery | undefined {
  if (!query) return undefined;
  const { learnerPage, learnerPageSize } = query;
  return omitEmptyQuery<TeachingPlanStatisticsGeneratedQuery>({
    ...(learnerPage === undefined ? {} : { learner_page: learnerPage }),
    ...(learnerPageSize === undefined ? {} : { learner_page_size: learnerPageSize }),
  });
}

const eduTeachingPlansApi = {
  async getTeachingPlansOverview(): Promise<EduTeachingPlansOverview> {
    return unwrapOpenApiResult(await eduAdminOpenApiClient.GET("/api/teaching-plans/overview"));
  },
  async getTeachingPlans(query?: EduTeachingPlansListQuery): Promise<EduTeachingPlansListResponse> {
    return unwrapOpenApiResult(
      await eduAdminOpenApiClient.GET("/api/teaching-plans", {
        params: { query: toTeachingPlansListQueryParams(query) },
      }),
    );
  },
  async createTeachingPlan(payload: EduTeachingPlanCreatePayload): Promise<CreateTeachingPlanResponse> {
    return unwrapOpenApiResult(await eduAdminOpenApiClient.POST("/api/teaching-plans", { body: payload }));
  },
  async getTeachingPlanDetail(planId: TeachingPlanId): Promise<EduTeachingPlanDetail> {
    return unwrapOpenApiResult(
      await eduAdminOpenApiClient.GET("/api/teaching-plans/{plan_id}", {
        params: { path: { plan_id: planId } },
      }),
    );
  },
  async getTeachingPlanStatistics(
    planId: TeachingPlanId,
    query?: EduTeachingPlanStatisticsQuery,
  ): Promise<EduTeachingPlanStatisticsResponse> {
    return unwrapOpenApiResult(
      await eduAdminOpenApiClient.GET("/api/teaching-plans/{plan_id}/statistics", {
        params: {
          path: { plan_id: planId },
          query: toTeachingPlanStatisticsQueryParams(query),
        },
      }),
    );
  },
  async updateTeachingPlanSnapshotCatalog(
    planId: TeachingPlanId,
    payload: EduTeachingPlanSnapshotCatalogUpdatePayload,
  ): Promise<UpdateTeachingPlanSnapshotCatalogResponse> {
    return unwrapOpenApiResult(
      await eduAdminOpenApiClient.PATCH("/api/teaching-plans/{plan_id}/snapshot-catalog", {
        params: { path: { plan_id: planId } },
        body: payload,
      }),
    );
  },
  async bindTeachingPlanMembers(
    planId: TeachingPlanId,
    payload: EduTeachingPlanMemberBindingPayload,
  ): Promise<BindTeachingPlanMembersResponse> {
    return unwrapOpenApiResult(
      await eduAdminOpenApiClient.PUT("/api/teaching-plans/{plan_id}/members", {
        params: { path: { plan_id: planId } },
        body: payload,
      }),
    );
  },
  async getTeachingPlanBoardThreads(planId: TeachingPlanId): Promise<EduTeachingPlanBoardThreadsResponse> {
    return unwrapOpenApiResult(
      await eduAdminOpenApiClient.GET("/api/teaching-plans/{plan_id}/board-threads", {
        params: { path: { plan_id: planId } },
      }),
    );
  },
  async createTeachingPlanBoardThread(
    planId: TeachingPlanId,
    payload: EduTeachingPlanBoardThreadCreatePayload,
  ): Promise<CreateTeachingPlanBoardThreadResponse> {
    return unwrapOpenApiResult(
      await eduAdminOpenApiClient.POST("/api/teaching-plans/{plan_id}/board-threads", {
        params: { path: { plan_id: planId } },
        body: payload,
      }),
    );
  },
  async getTeachingPlanNotices(planId: TeachingPlanId): Promise<EduTeachingPlanNoticesResponse> {
    return unwrapOpenApiResult(
      await eduAdminOpenApiClient.GET("/api/teaching-plans/{plan_id}/notices", {
        params: { path: { plan_id: planId } },
      }),
    );
  },
  async createTeachingPlanNotice(
    planId: TeachingPlanId,
    payload: EduTeachingPlanNoticeCreatePayload,
  ): Promise<CreateTeachingPlanNoticeResponse> {
    return unwrapOpenApiResult(
      await eduAdminOpenApiClient.POST("/api/teaching-plans/{plan_id}/notices", {
        params: { path: { plan_id: planId } },
        body: payload,
      }),
    );
  },
  async publishTeachingPlan(planId: TeachingPlanId): Promise<PublishTeachingPlanResponse> {
    return unwrapOpenApiResult(
      await eduAdminOpenApiClient.POST("/api/teaching-plans/{plan_id}/publish", {
        params: { path: { plan_id: planId } },
      }),
    );
  },
  async endTeachingPlan(planId: TeachingPlanId): Promise<EndTeachingPlanResponse> {
    return unwrapOpenApiResult(
      await eduAdminOpenApiClient.POST("/api/teaching-plans/{plan_id}/end", {
        params: { path: { plan_id: planId } },
      }),
    );
  },
  async getTeachingPlanTaskSubmissions(
    planId: TeachingPlanId,
    query?: EduTeachingPlanTaskSubmissionsQuery,
  ): Promise<EduTeachingPlanTaskSubmissionsResponse> {
    return unwrapOpenApiResult(
      await eduAdminOpenApiClient.GET("/api/teaching-plans/{plan_id}/task-submissions", {
        params: {
          path: { plan_id: planId },
          query: toTeachingPlanTaskSubmissionsQueryParams(query),
        },
      }),
    );
  },
  async reviewTeachingPlanTaskSubmission(
    planId: TeachingPlanId,
    submissionId: TeachingPlanSubmissionId,
    payload: EduTeachingPlanTaskSubmissionReviewPayload,
  ): Promise<EduTeachingPlanTaskSubmissionReviewResponse> {
    return unwrapOpenApiResult(
      await eduAdminOpenApiClient.PATCH(
        "/api/teaching-plans/{plan_id}/task-submissions/{submission_id}/review",
        {
          params: { path: { plan_id: planId, submission_id: submissionId } },
          body: payload,
        },
      ),
    );
  },
  async resetCourseLearningForMember(
    courseId: CourseId,
    memberId: MemberId,
  ): Promise<EduCourseLearningResetResponse> {
    return unwrapOpenApiResult(
      await eduAdminOpenApiClient.POST("/api/learning-records/courses/{course_id}/members/{member_id}/reset", {
        params: { path: { course_id: courseId, member_id: memberId } },
      }),
    );
  },
  async resetCourseLearningForMembers(
    courseId: CourseId,
    memberIds: OperationRequestBody<BulkResetCourseLearningOperation>["member_ids"],
  ): Promise<EduCourseLearningBulkResetResponse> {
    return unwrapOpenApiResult(
      await eduAdminOpenApiClient.POST("/api/learning-records/courses/{course_id}/members/reset", {
        params: { path: { course_id: courseId } },
        body: { member_ids: memberIds },
      }),
    );
  },
};

export default eduTeachingPlansApi;
